import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AUDIT_STATUS_ICON_MAP, auditToColor } from '@root/entities/pair/ui'
import { formatLiquidityBurned, NumericFormat } from '@root/shared/core/format'
import { WarningOutlinedIcon, Separator } from '@root/shared/ui-kit'

import type { PairAudit } from '@root/entities/pair'

const SHOW_UPDATE_AUTHORITY = Boolean(false)

type AuditWarning = {
  label: string
  value: string
  status: PairAudit.AuditStatus
}
type Props = {
  audit: PairAudit.Audit
}

export const TradingAuditHeading: React.FC<Props> = ({ audit }) => {
  const { t } = useTranslation()

  const warnings = useMemo(() => {
    const result: Array<AuditWarning> = []

    if (audit.checks.isMintable.status !== 'Safe') {
      result.push({
        label: t('trading.audit.is_mintable'),
        value: t('common.yes'),
        status: audit.checks.isMintable.status,
      })
    }

    if (
      'isTokenDataMutable' in audit.checks &&
      audit.checks.isTokenDataMutable.status !== 'Safe'
    ) {
      result.push({
        label: t('trading.audit.is_token_data_mutable'),
        value: t('common.yes'),
        status: audit.checks.isTokenDataMutable.status,
      })
    }

    if (audit.checks.isFreezable.status !== 'Safe') {
      result.push({
        label: t('trading.audit.is_freezable'),
        value: t('common.yes'),
        status: audit.checks.isFreezable.status,
      })
    }

    const updateAuthority = audit.checks.updateAuthority
    if (updateAuthority.status !== 'Safe' && SHOW_UPDATE_AUTHORITY) {
      result.push({
        label: t('trading.audit.update_authority'),
        value:
          updateAuthority.value === 'None'
            ? t('trading.audit.update_authority.none')
            : updateAuthority.value.slice(-6),
        status: updateAuthority.status,
      })
    }

    if (audit.checks.top10HoldersPercentage.status !== 'Safe') {
      result.push({
        label: t('trading.audit.top_10_holders'),
        value: `${NumericFormat.formatPreset(
          audit.checks.top10HoldersPercentage.percent,
          'percents',
        )}%`,
        status: audit.checks.top10HoldersPercentage.status,
      })
    }

    if (audit.checks.lpBurned.status !== 'Safe') {
      result.push({
        label: t('trading.audit.lp_burned'),
        value: `${formatLiquidityBurned(audit.checks.lpBurned.percent)}%`,
        status: audit.checks.lpBurned.status,
      })
    }

    if (audit.checks.ownerTokenBalance.status !== 'Safe') {
      result.push({
        label: t('trading.audit.dev_holding'),
        value: `${NumericFormat.formatPreset(
          audit.checks.ownerTokenBalance.percent,
          'percents',
        )}%`,
        status: audit.checks.ownerTokenBalance.status,
      })
    }

    return result
  }, [audit, t])

  const renderWarnings = () => {
    return (
      <>
        <Separator />
        <Warnings>
          {warnings.map((warning) => (
            <Warning key={warning.label} data-intent={warning.status}>
              {warning.label}

              <WarningValue>
                <WarningSeparator />
                {warning.value}
                <WarningOutlinedIcon size={18} />
              </WarningValue>
            </Warning>
          ))}
        </Warnings>
      </>
    )
  }

  const Icon = AUDIT_STATUS_ICON_MAP[audit.overAllChecks.overall]

  return (
    <Root $status={audit.overAllChecks.overall}>
      <Title>
        <Icon size={18} />
        {warnings.length > 0 ? (
          <>
            {t('trading.audit.title_warnings', {
              count: warnings.length,
            })}
          </>
        ) : (
          t('trading.audit.title')
        )}
      </Title>
      {renderWarnings()}
    </Root>
  )
}

const Root = styled.div<{ $status: PairAudit.AuditStatus }>`
  display: grid;
  gap: 10px;
  --title-color: ${({ $status: status }) => auditToColor(status)};
`

const Title = styled.h3`
  color: var(--title-color);

  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: center;
  place-items: center;

  gap: 6px;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  text-wrap: balance;
`

const Warnings = styled.div`
  display: grid;
  gap: 10px;
`

const Warning = styled.p`
  color: var(--warning-9);
  text-align: center;

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-items: end;
  gap: 6px;

  &[data-intent='Warning'] {
    color: var(--warning-9);
  }
  &[data-intent='Danger'] {
    color: var(--error-9);
  }
`

const WarningSeparator = styled.span`
  border-radius: 2px;
  background: var(--text-7);
  width: 12px;
  height: 1px;
`

const WarningValue = styled.span`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  justify-self: start;
  gap: 6px;
`
